// @flow

import _ from 'lodash/fp';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {apiBase} from '../../helpers/request';
import {
  applyFilter,
  fetchData,
} from '../../actions/purchaseReportView';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import SelectFormInput from '../../components/SelectFormInput/SelectFormInput';
import booleanValues from '../../booleanValues';

import type {PurchaseReportState} from '../../reducers/purchaseReportView';
import type {
  Entity,
  Project,
} from '../../actions/purchaseReportView';

const fileUrl = `${apiBase}/files`;

export const getUrl = (projectId: number, apartmentIds: number[], supplierId?: ?number, includeFileName?: boolean = true, withPlans?: boolean = false, linebreakPerApartment?: boolean = false) => {
  let url = `${fileUrl}/export/projects/${projectId}/purchase`;
  if (!!includeFileName) {
    url += `/project_${projectId}_purchase_export.pdf`;
  }

  const params = [];
  if (!!apartmentIds.length) {
    params.push(`apartmentIds=${_.join(',', apartmentIds)}`);
  }

  if (!!withPlans) {
    params.push('withPlans=1');
  }

  if (!!linebreakPerApartment) {
    params.push('linebreakPerApartment=1');
  }

  if (!!supplierId) {
    params.push(`supplierId=${supplierId}`);
  }

  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }

  return url;
};

export type Props = {
  apartmentIds: number[],
  apartments: any[],
  applyFilter: typeof applyFilter,
  fetchData: typeof fetchData,
  isFetching: boolean,
  projectId?: number,
  projects: Project[],
  supplierId?: number,
  suppliers: any[],
  includePlans: boolean,
  linebreakPerApartment: boolean,
};

class PurchaseOrderReportContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    this.props.fetchData();
  }

  render() {
    const {
      apartmentIds,
      apartments,
      applyFilter,
      isFetching,
      projectId,
      projects,
      suppliers,
      supplierId,
      includePlans,
      linebreakPerApartment,
    } = this.props;

    return (
      <div className="PurchaseOrderReportContainer relative">
        <LoadingIndicator isLoading={isFetching} />
        <div className="row">
          <div className="column">
            <SelectFormInput
              input={{
                value: projectId,
                onBlur: _.noop,
                onChange: project => applyFilter(apartmentIds, project ? project.id : null, supplierId, includePlans, linebreakPerApartment),
              }}
              placeholder="Välj projekt"
              meta={{}}
              valueKey="id"
              labelKey="name"
              multi={false}
              options={projects}
              clearable={false}
              isLoading={isFetching} />
          </div>
          <div className="column">
            <SelectFormInput
              input={{
                value: apartmentIds,
                onBlur: _.noop,
                onChange: (apartments: Entity[]) =>
                  applyFilter(
                    _.map('id', apartments),
                    projectId,
                    supplierId,
                    (apartments || []).length === 1
                      ? includePlans
                      : false,
                    linebreakPerApartment,
                  ),
              }}
              placeholder="Välj lägenheter"
              meta={{}}
              valueKey="id"
              labelKey="objectId"
              multi={true}
              options={apartments}
              clearable={true}
              isLoading={isFetching}
              keepOpen={true} />
            {apartmentIds.length === 1 &&
              <div className="row">
                <div className="column">
                  <SelectFormInput
                    input={{
                      value: !!includePlans,
                      onBlur: _.noop,
                      onChange: includePlans =>
                        applyFilter(
                          apartmentIds,
                          projectId,
                          supplierId,
                          includePlans
                            ? (apartmentIds.length === 1 && includePlans.value)
                            : false,
                          linebreakPerApartment,
                        ),
                    }}
                    label="Inkludera ritningar"
                    meta={{}}
                    valueKey="value"
                    labelKey="label"
                    clearable={false}
                    options={booleanValues} />
                </div>
              </div>
            }
            {(apartmentIds.length === 0 || apartmentIds.length > 1) &&
              <div className="row">
                <div className="column">
                  <SelectFormInput
                    input={{
                      value: !!linebreakPerApartment,
                      onBlur: _.noop,
                      onChange: linebreakPerApartment =>
                        applyFilter(
                          apartmentIds,
                          projectId,
                          supplierId,
                          includePlans,
                          linebreakPerApartment
                            ? ((apartmentIds.length === 0 || apartmentIds.length > 1) && linebreakPerApartment.value)
                            : false
                        ),
                    }}
                    label="Radbrytning för varje lägenhet"
                    meta={{}}
                    valueKey="value"
                    labelKey="label"
                    clearable={false}
                    options={booleanValues} />
                </div>
              </div>
            }
          </div>
          <div className="column">
            <SelectFormInput
              input={{
                value: supplierId,
                onBlur: _.noop,
                onChange: supplier => applyFilter(apartmentIds, projectId, supplier ? supplier.id : null, includePlans, linebreakPerApartment),
              }}
              placeholder="Välj leverantör"
              meta={{}}
              valueKey="id"
              labelKey="name"
              multi={false}
              options={suppliers}
              clearable={true}
              isLoading={isFetching} />
          </div>
          {!!projectId &&
            <div className="column shrink">
              <a
                href={getUrl(projectId, apartmentIds, supplierId, true, includePlans, linebreakPerApartment)}
                target="_blank"
                className="button success">
                Ladda ner
              </a>
            </div>
          }
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({
  purchaseReportView: {
    apartmentIds,
    apartments,
    isFetching,
    projectId,
    projects,
    supplierId,
    suppliers,
    includePlans,
    linebreakPerApartment,
  },
}: {purchaseReportView: PurchaseReportState}) => ({
  apartmentIds,
  apartments,
  isFetching,
  projectId,
  projects,
  supplierId,
  suppliers,
  includePlans,
  linebreakPerApartment,
});

const mapDispatchToProps = dispatch => ({
  applyFilter: (apartmentIds, projectId, supplierId, includePlans, linebreakPerApartment) =>
    dispatch(applyFilter(apartmentIds, projectId, supplierId, includePlans, linebreakPerApartment)),
  fetchData: () =>
    dispatch(fetchData()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PurchaseOrderReportContainer);



// WEBPACK FOOTER //
// ./src/containers/PurchaseOrderReportContainer/PurchaseOrderReportContainer.js